import VolumeInput from "@components/form/VolumeInput";
import WorkflowSteps from "@components/zenith/ui/WorkflowSteps";
import StepActions from "@components/zenith/ui/StepActions";
import WorkflowCreateForwardDrawCompleted from "@components/form/workflows/create_forward_draw/WorkflowCreateForwardDrawCompleted";
import WorkflowCreateForwardDrawStep1 from "@components/form/workflows/create_forward_draw/WorkflowCreateForwardDrawStep1";
import WorkflowCreateForwardDrawStep2 from "@components/form/workflows/create_forward_draw/WorkflowCreateForwardDrawStep2";
import WorkflowCreateForwardDrawStep3 from "@components/form/workflows/create_forward_draw/WorkflowCreateForwardDrawStep3";
import { useAppContext } from "@context/AppContext";
import {
  ForwardDrawProvider,
  useForwardDrawContext,
} from "@context/ForwardDrawContext";
import { EvidenceProvider } from "@context/shared/EvidenceContext";
import { useMessageTemplates } from "@hooks/query/useMessageTemplates";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const WorkflowCreateForwardDraw: FunctionComponent = () => {
  return (
    <ForwardDrawProvider>
      <EvidenceProvider>
        <MainComponent />
      </EvidenceProvider>
    </ForwardDrawProvider>
  );
};

const MainComponent: FunctionComponent = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const {
    loggedInInfo: { userDetails },
  } = useAppContext();
  const navigate = useNavigate();
  const cancel = () => navigate("/zenith/workflows");
  const { details, setDetails, setMessage } = useForwardDrawContext();
  // Stable, because VolumeInput re-runs an effect whenever it changes.
  const setVolumes = useCallback(
    (volumes: number[]) => setDetails((prev: any) => ({ ...prev, volumes })),
    [setDetails],
  );

  useMessageTemplates({
    enabled: !!userDetails,
    onSuccess: (data: any[]) => {
      const messageTemplate = data.find(
        t => t.identifier === "ForwardDrawRequest",
      );

      if (messageTemplate) {
        setMessage({
          messageTemplate,
          subject: t(messageTemplate.subjectTextTemplate),
          body: t(messageTemplate.bodyTextTemplate, {
            recipientName: t("common.administrators"),
            senderName: userDetails?.name,
            subscriberAccountNumber: userDetails?.subscriber?.accountNumber,
          }),
        });
      }
    },
  });

  // Merge: userDetails changes mid-wizard whenever a session refetch returns
  // edited details, and replacing would drop what the customer entered.
  useEffect(() => {
    if (userDetails)
      setDetails((prev: any) => ({
        ...prev,
        subscribers: [userDetails.subscriber],
      }));
  }, [userDetails, setDetails]);

  return (
    <WorkflowSteps
      title={t("zenith.workflows.create_forward_draw")}
      parent={{
        label: t("zenith.navigation.workflows"),
        to: "/zenith/workflows",
      }}
      selectedStep={currentStep}
      onStepButtonClick={step => setCurrentStep(step)}
      steps={[
        {
          panel: (
            <WorkflowCreateForwardDrawStep1
              onSubmit={() => setCurrentStep(1)}
            />
          ),
        },
        {
          panel: (
            <VolumeInput
              selectedExtractionRights={details.extractionRights}
              volumes={details.volumes}
              onVolumeChange={setVolumes}
              onPrevious={() => setCurrentStep(0)}
              onNext={() => setCurrentStep(2)}
              onCancel={cancel}
              fixedRate
              footer={
                <StepActions
                  onPrevious={() => setCurrentStep(0)}
                  onCancel={cancel}
                  onNext={() => setCurrentStep(2)}
                />
              }
            />
          ),
        },
        {
          panel: (
            <WorkflowCreateForwardDrawStep2
              onCancel={() => setCurrentStep(1)}
              onSubmit={() => setCurrentStep(3)}
            />
          ),
        },
        {
          panel: (
            <WorkflowCreateForwardDrawStep3
              onCancel={() => setCurrentStep(2)}
              onSubmit={() => setCurrentStep(4)}
            />
          ),
        },
      ]}
      completion={{ panel: <WorkflowCreateForwardDrawCompleted /> }}
    />
  );
};

export default WorkflowCreateForwardDraw;
